import type { RingFinishEnum } from '@jouzen/ecom-components/v3/enums/ringFinish';
import { useEffect, useState } from 'react';

import {
  type PDPContextType,
  usePDPContext,
} from '@/app/[locale]/product/_contexts/PDPProvider';
import MembershipSelection from '@/app/[locale]/product/_steps/enums/MembershipSelection';
import SizeSelection from '@/app/[locale]/product/_steps/enums/SizeStep';
import WarrantySelection from '@/app/[locale]/product/_steps/enums/WarrantySelection';
import getCurrentStepFromRoute from '@/app/[locale]/product/_utils/getCurrentStepFromRoute';
import getExtendedWarrantyStep from '@/app/[locale]/product/_utils/getExtendedWarrantyStep';
import {
  EXTENDED_WARRANTY_2YEAR_SKU,
  EXTENDED_WARRANTY_3YEAR_SKU,
  MEMBERSHIP_1YEAR_SKU,
} from '@/app/[locale]/store/_consts';
import useContentScroll from '@/app/[locale]/store/_hooks/useContentScroll';
import getExistingMembership from '@/app/[locale]/store/_utils/getExistingMembership';
import getExistingWarranty from '@/app/[locale]/store/_utils/getExistingWarranty';
import {
  DEFAULT_REFRESH_CART_VALUES,
  useCartContext,
} from '@/app/contexts/CartProvider';
import { type RingStyleEnum } from '@/app/enums/RingStyleEnum';

import type UseProducts from '../types';
import { useButtonDisabled } from '../useButtonDisabled';
import useProducts from '../useProducts';
import type { UseWizardState, UseWizardStateProps } from './types';

// Implement the useWizardState
const useWizardState = ({
  currentRoute,
}: UseWizardStateProps): UseWizardState => {
  // choose between sizing kit and choose size
  const [sizingSelector, setSizingSelector] = useState<SizeSelection>(
    SizeSelection.SizingKit,
  );

  // add to cart loading state
  const [isAddingToCart, setIsAddingToCart] = useState<boolean>(false);

  // membership and warranty selection
  const [membershipSelection, setMembershipSelection] =
    useState<MembershipSelection>(MembershipSelection.OneMonth);
  const [warrantySelection, setWarrantySelection] = useState<WarrantySelection>(
    WarrantySelection.OneYear,
  );

  const [notifyModal, setNotifyModal] = useState<boolean>(false);

  // scroll to choose button on click
  const contentScroller = useContentScroll();

  // current step from route
  const currentStep = getCurrentStepFromRoute(currentRoute);
  const products: UseProducts = useProducts(currentRoute);

  const { getSizeStockStatus, isLoadingPdpData }: PDPContextType =
    usePDPContext();
  const { refreshCart } = useCartContext();
  const cart =
    refreshCart?.cart?.refreshCart.cart ??
    DEFAULT_REFRESH_CART_VALUES.cart!.refreshCart.cart!;
  const chargerCount = cart.chargerCount!;
  const itemCountForIcon = cart.itemCountForIcon!;
  const maxChargerQuantity = cart.maxChargerQuantity!;
  const maxRingQuantity = cart.maxRingQuantity!;
  const ringCount = cart.ringCount!;

  const { finish, isAccessory, size, style } = currentRoute;
  const stockStatus = getSizeStockStatus({
    style: style.value as RingStyleEnum,
    finish: finish.value as RingFinishEnum,
    selectedSize: size.value,
  });

  const existingWarranty = getExistingWarranty(refreshCart);

  // set existing warranty selection
  useEffect(() => {
    if (existingWarranty) {
      const sku = existingWarranty.skuCode;
      if (sku === EXTENDED_WARRANTY_2YEAR_SKU) {
        setWarrantySelection(WarrantySelection.TwoYear);
      } else if (sku === EXTENDED_WARRANTY_3YEAR_SKU) {
        setWarrantySelection(WarrantySelection.ThreeYear);
      } else {
        setWarrantySelection(WarrantySelection.OneYear);
      }
    }
  }, [existingWarranty]);

  const existingMembership = getExistingMembership(refreshCart);

  // set existing membership selection
  useEffect(() => {
    if (existingMembership) {
      const sku = existingMembership.skuCode;
      if (sku === MEMBERSHIP_1YEAR_SKU) {
        setMembershipSelection(MembershipSelection.OneYear);
      } else {
        setMembershipSelection(MembershipSelection.OneMonth);
      }
    }
  }, [existingMembership]);

  const warrantyStep = getExtendedWarrantyStep(existingWarranty?.skuCode);

  // cart is full messaging
  const hasMaxRingCount = ringCount >= maxRingQuantity && !isAccessory;
  const hasMaxChargerCount = chargerCount >= maxChargerQuantity && isAccessory;

  const nextButtonDisabled = useButtonDisabled({
    currentStep,
    sizingSelector,
    size,
    isAccessory,
    isLoadingPdpData,
    stockStatus,
  });

  return {
    ...contentScroller,
    ...products,
    chargerCount,
    currentRoute,
    currentStep,
    existingMembership,
    existingWarranty,
    hasMaxChargerCount,
    hasMaxRingCount,
    isAccessory,
    isAddingToCart,
    itemCountForIcon,
    membershipSelection,
    nextButtonDisabled,
    notifyModal,
    ringCount,
    sizingSelector,
    stockStatus,
    warrantySelection,
    warrantyStep,
    setIsAddingToCart,
    setMembershipSelection,
    setNotifyModal,
    setSizingSelector,
    setWarrantySelection,
  };
};

export default useWizardState;
